/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { format, isMobile, VariantType } from '@msdyn365-commerce-modules/utilities';
import * as MsDyn365 from '@msdyn365-commerce/core';
import moment from 'moment';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { signUp_DLX_V3Async } from '../../actions/DataActionExtension.g';
import DateOfBirthInput, { getDateMask } from '../../common/components/date-of-birth-input';
import PhoneInputComponent from '../../common/components/phone-number-input';
import { getLoyaltySchema } from '../../schema/loyalty-store-sign-up';
import { MIN_AGE_FOR_SIGNUP, TEMPORARY_ACCOUNT_NUMBER_LENGTH } from '../../themes/venchi-v2-1/utils/constants';
import {
    determineSiteLanguageForAbandonedCart,
    getSiteLanguageConfigFromContext
} from '../../themes/venchi-v2-1/utils/determineSiteLanguageForAbandonedCart';
import { textWithLineBreaks } from '../../themes/venchi-v2-1/utils/textWithLineBreaks';
import { ILoyaltyStoreSignUpViewProps } from './loyalty-store-sign-up';

// See this Coda page for a mapped comparison of locale requirements:
// https://coda.io/d/Product-team_dEbHeHujlIi/Loyalty-per-country-specs-updated-on-2025-09-04_sudv2AgE?docIds=all#_luo9wPkC

// Define the component
const LoyaltyStoreSignUpView: React.FC<ILoyaltyStoreSignUpViewProps> = props => {
    // Destructure props for easier access
    const {
        loyaltyStoreSignUpMandatoryAgreement,
        additionalFieldsHeading,
        birthdaySubtext,
        postalCodeSubtext,
        phoneNumberSubtext,
        // loyaltyVCLUBDetailsLinkText,
        feedbackAlreadySubscribed,
        feedbackCardBeingGenerated,
        feedbackSuccess,
        privacyPolicyURL,
        textGenericMarketing,
        // walletLoyaltyProgramName,
        loyaltyCouponCode,
        loyaltyBarcodeImage,
        couponCodeText,
        showFlagGenericMarketing,
        showFlagProfiling,
        showFlagThirdParty,
        useCrossCountryLabels
    } = props.config;

    const {
        vClubCard,
        joinedLoyaltyThankYouHeader,
        loyaltyStoreSignUpSubmitButtonLabel,
        loyaltyRegistrationDateText,
        htmlGenericMarketing,
        htmlProfiling,
        htmlThirdParty,
        htmlGenericMarketingCrossCountry,
        htmlProfilingCrossCountry,
        htmlThirdPartyCrossCountry,
        birthdayLabelText,
        addressZipCodeLabelOptional,
        phoneLabelText,
        emailAddressLabelText,
        emailErrortext,
        feedbackErrorText,
        feedbackNewsletterOnly,
        feedbackLoyaltyNotAvailable,
        feedbackServiceUnavailable,
        feedbackThrottled,
        loyaltyBarcodeText
    } = props.resources;

    const siteLanguageConfig = getSiteLanguageConfigFromContext(props.context);
    const localeString = siteLanguageConfig.currentLocale;

    // Normalize locale into a small set of categories so we can switch behaviour easily
    let formLayoutRendered: 'US' | 'AU' | 'default' = 'default';
    switch ((localeString || '').toLowerCase()) {
        case 'en-us':
            formLayoutRendered = 'US';
            break;
        case 'en-au':
            formLayoutRendered = 'AU';
            break;
        default:
            formLayoutRendered = 'default';
    }
    const isUS = formLayoutRendered === 'US';
    const isAU = formLayoutRendered === 'AU';

    React.useEffect(() => {
        console.log({ formLayoutRendered });
    }, [formLayoutRendered]);

    // Normalize enableSignUp coming from authoring (can be boolean or string like "false"/"true")
    const enableSignUp: boolean = String(props.config.enableSignUp).toLowerCase() === 'true';
    React.useEffect(() => {
        console.log(`localeString: ${localeString}, enableSignUp: ${enableSignUp}`);
    }, [localeString, enableSignUp]);

    const schema = React.useMemo(() => {
        const newSchema = getLoyaltySchema(localeString, enableSignUp);
        return newSchema;
    }, [localeString, enableSignUp]);

    type FormInputs = z.infer<ReturnType<typeof getLoyaltySchema>>;

    const onlyGenericMarketingFlag = showFlagGenericMarketing && !showFlagProfiling && !showFlagThirdParty;

    // Determine which consent texts to use based on configuration
    const effectiveHtmlGenericMarketing = useCrossCountryLabels ? htmlGenericMarketingCrossCountry : htmlGenericMarketing;
    const effectiveHtmlProfiling = useCrossCountryLabels ? htmlProfilingCrossCountry : htmlProfiling;
    const effectiveHtmlThirdParty = useCrossCountryLabels ? htmlThirdPartyCrossCountry : htmlThirdParty;

    const resolver = React.useMemo(() => {
        return zodResolver(schema);
    }, [schema]);

    const {
        register,
        handleSubmit,
        formState: { errors, isValid },
        watch,
        setValue,
        clearErrors,
        reset
    } = useForm<FormInputs>({
        resolver,
        mode: 'onChange',
        reValidateMode: 'onChange',
        shouldFocusError: true
    });

    // Reset form when schema changes to ensure proper validation state
    React.useEffect(() => {
        reset();
    }, [schema, reset]);

    // This error is shown under the register button.
    const [phoneErrorMessage, setPhoneErrorMessage] = React.useState('');

    // const programString: string = walletLoyaltyProgramName || 'v-club-emea';

    // State variables for loading, disabled state, and subscription message
    const [loading, setLoading] = React.useState(false);
    const [disabled_state, setDisabled] = React.useState(false);
    const check_ref = React.useRef<HTMLInputElement>(null);
    const [submittedEmailAddress, setSubmittedEmailAddress] = React.useState<string | null>(null);
    const [loyaltyCardNumber, setLoyaltyCardNumber] = React.useState<string | null>(null);
    const [loyaltyEnrollmentDate, setLoyaltyEnrollmentDate] = React.useState<Date | null>(null);
    const [subscribeMessage, setSubscribeMessage] = React.useState({
        state: false,
        message: '',
        class: ''
    });
    const [showPassword, setShowPassword] = React.useState(false);
    const [showInvalidBirthdayFormatError, setShowInvalidBirthdayFormatError] = React.useState(false);
    const [showMinAgeError, setShowMinAgeError] = React.useState(false);

    const isMobileDevice =
        isMobile({ variant: VariantType.Browser, context: props.context.request }) === 'xs' ||
        isMobile({ variant: VariantType.Viewport, context: props.context.request }) === 'xs' ||
        isMobile({ variant: VariantType.Browser, context: props.context.request }) === 'sm' ||
        isMobile({ variant: VariantType.Viewport, context: props.context.request }) === 'sm';

    // Extract query parameters from the request context
    const query_dict = props.context.request.query as Object;
    const queryParameter = Object.keys(query_dict)[0];
    const store_code = Object.values(query_dict)[0];
    const store_kiosk = Object.keys(query_dict).indexOf('kiosk') > -1 ? true : false;

    const hostname = siteLanguageConfig.hostname;
    const sitePath = props?.context?.request?.sitePath ? props?.context?.request?.sitePath?.substring(1) : '';

    const regionCRM = determineSiteLanguageForAbandonedCart(siteLanguageConfig);

    // Define constants for hostname and locale
    // const loyalty_URL = `https://${hostname}/${sitePath}/loyalty?hasJoined=true`;

    // Default unchecked for non-US locales
    const [unchecked_state, setUncheckedState] = React.useState(!isUS);

    const watchEmail = watch('email');
    const watchForm = watch();

    const validateAge = React.useCallback((isoDate: string): boolean => {
        if (!isoDate) {
            return false;
        }

        const birthdayDate = moment(isoDate, 'YYYY-MM-DD', true);
        if (!birthdayDate.isValid()) {
            return false;
        }

        const today = moment();
        const age = today.diff(birthdayDate, 'years');

        return age >= MIN_AGE_FOR_SIGNUP;
    }, []);

    const handleBirthdayChange = React.useCallback(
        (isoDate: string, isBirthdayValid: boolean) => {
            const normalizedBirthday = isBirthdayValid ? isoDate : '';

            setValue('birthday', normalizedBirthday, {
                shouldDirty: true,
                shouldValidate: true
            });

            if (normalizedBirthday) {
                const isAdult = validateAge(normalizedBirthday);
                setShowMinAgeError(!isAdult);
                setShowInvalidBirthdayFormatError(false);
            } else {
                setShowMinAgeError(false);
            }

            if (normalizedBirthday) {
                clearErrors('birthday');
            }
        },
        [setValue, clearErrors, validateAge]
    );

    const handleBirthdayBlur = React.useCallback(
        (isoDate: string, isBirthdayValid: boolean, isFilled?: boolean) => {
            if (isBirthdayValid && isoDate) {
                const isAdult = validateAge(isoDate);
                setShowMinAgeError(!isAdult);
                setShowInvalidBirthdayFormatError(false);
                return;
            }

            if (isFilled) {
                setShowInvalidBirthdayFormatError(true);
                setShowMinAgeError(false);
            } else {
                setShowInvalidBirthdayFormatError(false);
                setShowMinAgeError(false);
            }
        },
        [validateAge]
    );

    // Debug validation state in detail
    React.useEffect(() => {
        console.group('🔍 Form Validation Debug');
        console.log('isValid:', isValid);
        console.log('errors:', errors);
        console.log('form values:', watchForm);
        console.log('schema type:', typeof schema);
        console.log('resolver type:', typeof resolver);

        // Test manual validation
        if (watchForm && Object.keys(watchForm).length > 0) {
            try {
                const manualValidation = schema.safeParse(watchForm);
                console.log('Manual schema validation:', manualValidation);
                if (!manualValidation.success) {
                    console.log('Manual validation ERRORS:', manualValidation.error.issues);
                }
            } catch (e) {
                console.log('Manual validation error:', e);
            }
        }
        console.groupEnd();
    }, [isValid, errors, watchForm, schema, resolver]);

    // Function to handle form submission
    const _submit = async (data: any) => {
        if (phoneErrorMessage) {
            return;
        }

        setDisabled(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setSubmittedEmailAddress(data.email);
        setLoading(true);
        let birthday_slash: string = data.birthday;
        birthday_slash = birthday_slash.replace('-', '/').replace('-', '/');
        let message: string;

        const context = props.context;

        if (!enableSignUp) {
            if (isAU) {
                signUp_DLX_V3Async(
                    { callerContext: context.actionContext },
                    data.name || '',
                    data.lastname || '',
                    data.email,
                    '', // Password - empty for loyalty signup
                    !isUS ? data.phone_number || '' : `+1${data.phone_number}`,
                    true,
                    data.marketing,
                    onlyGenericMarketingFlag ? data.marketing : data.profiling,
                    onlyGenericMarketingFlag ? false : data.thirdparty,
                    localeString,
                    birthday_slash,
                    data.postal_code,
                    store_code ? `Store_${store_code}` : 'Store_fallback',
                    regionCRM
                )
                    .then(result => {
                        // Log debugging information
                        console.log('SignUp_DLX_V3 response (AU):', {
                            status: result.status,
                            messageKey: result.messageKey,
                            errorCode: result.errorCode,
                            correlationId: result.correlationId,
                            checkpoints: result.checkpoints
                        });

                        // Handle error scenarios based on status and errorCode
                        if (result.status && result.status !== 200) {
                            // Map error codes to user messages
                            switch (result.errorCode) {
                                case 'LOYALTY_SCHEME_NOT_AVAILABLE':
                                    throw new Error('loyalty_not_available');
                                case 'DUPLICATE_ENROLMENT': {
                                    // Extract card number for duplicate enrollment
                                    const existingCardNumber = result.loyaltyCards?.[0]?.CardNumber;
                                    if (existingCardNumber) {
                                        throw new Error(`already_existing:${existingCardNumber}`);
                                    } else {
                                        throw new Error('already_existing');
                                    }
                                }
                                case 'RTS_UNAVAILABLE':
                                    throw new Error('service_unavailable');
                                case 'GRAPH_THROTTLED':
                                    throw new Error('throttled');
                                default:
                                    throw new Error(result.errorCode || 'unknown_error');
                            }
                        }

                        // Handle success scenarios based on messageKey
                        switch (result.messageKey) {
                            case 'vclub.success':
                                // Full success - loyalty card issued
                                if (
                                    result.loyaltyCards &&
                                    result.loyaltyCards.length > 0 &&
                                    result.loyaltyCards[0].OmOperatingUnitId !== 0
                                ) {
                                    if (result.loyaltyCards[0].CardNumber?.length) {
                                        setLoyaltyCardNumber(result.loyaltyCards[0].CardNumber ?? null);
                                        setLoyaltyEnrollmentDate(result.loyaltyCards[0].LoyaltyEnrollmentDateLocal ?? null);
                                    }
                                    message = feedbackSuccess ?? 'Thank you! Please check your email';
                                    setSubscribeMessage({ state: true, message: message, class: 'success' });
                                } else if (result.customerGuid && result.customerGuid.length > 0) {
                                    // New customer, card being generated
                                    message = feedbackSuccess ?? 'Thank you! Please check your email';
                                    setSubscribeMessage({ state: true, message: message, class: 'success' });
                                } else if (result.accountNum?.length === TEMPORARY_ACCOUNT_NUMBER_LENGTH) {
                                    // Account not synced yet
                                    throw new Error('generating');
                                } else if (result.accountNum?.length === 10) {
                                    // Account synced
                                    if (
                                        result.loyaltyCards &&
                                        result.loyaltyCards.length > 0 &&
                                        result.loyaltyCards[0].OmOperatingUnitId !== 0
                                    ) {
                                        if (result.loyaltyCards[0].CardNumber?.length) {
                                            setLoyaltyCardNumber(result.loyaltyCards[0].CardNumber ?? null);
                                            setLoyaltyEnrollmentDate(result.loyaltyCards[0].LoyaltyEnrollmentDateLocal ?? null);
                                        }
                                        message = feedbackSuccess ?? 'Thank you! Please check your email';
                                        setSubscribeMessage({ state: true, message: message, class: 'success' });
                                    } else {
                                        throw new Error('already_existing');
                                    }
                                } else {
                                    message = feedbackSuccess ?? 'Thank you! Please check your email';
                                    setSubscribeMessage({ state: true, message: message, class: 'success' });
                                }
                                break;
                            case 'vclub.newsletterOnly':
                                // Partial success - newsletter only
                                message = feedbackNewsletterOnly ?? 'Newsletter subscription confirmed. Loyalty card was not issued.';
                                setSubscribeMessage({ state: true, message: message, class: 'generic' });
                                break;
                            case 'vclub.duplicate': {
                                // Duplicate enrollment - show existing card number
                                const existingCardNumber = result.loyaltyCards?.[0]?.CardNumber;
                                if (existingCardNumber) {
                                    throw new Error(`already_existing:${existingCardNumber}`);
                                } else {
                                    throw new Error('already_existing');
                                }
                            }
                            default:
                                message = feedbackSuccess ?? 'Thank you! Please check your email';
                                setSubscribeMessage({ state: true, message: message, class: 'success' });
                        }
                        setLoading(false);
                    })
                    .catch(err => {
                        console.error('SignUp_DLX_V3 error (AU):', err);
                        const errorMessage = err.message || '';

                        // Check if error contains card number (format: "already_existing:V00992890")
                        if (errorMessage.startsWith('already_existing:')) {
                            const cardNumber = errorMessage.split(':')[1];
                            message = feedbackAlreadySubscribed ?? 'The inserted email is already subscribed to our loyalty program.';
                            message += ` ${cardNumber}`;
                            setSubscribeMessage({ state: true, message: message, class: 'error' });
                        } else {
                            switch (errorMessage) {
                                case 'generating':
                                    message =
                                        feedbackCardBeingGenerated ?? 'Your card is already being generated, thank you for your patience.';
                                    setSubscribeMessage({ state: true, message: message, class: 'generic' });
                                    break;
                                case 'already_existing':
                                    message =
                                        feedbackAlreadySubscribed ?? 'The inserted email is already subscribed to our loyalty program.';
                                    setSubscribeMessage({ state: true, message: message, class: 'error' });
                                    break;
                                case 'loyalty_not_available':
                                    message = feedbackLoyaltyNotAvailable ?? 'Loyalty is not available for this store.';
                                    setSubscribeMessage({ state: true, message: message, class: 'error' });
                                    break;
                                case 'service_unavailable':
                                    message =
                                        feedbackServiceUnavailable ?? "We're having trouble connecting to the service. Please try again.";
                                    setSubscribeMessage({ state: true, message: message, class: 'error' });
                                    break;
                                case 'throttled':
                                    message = feedbackThrottled ?? 'Too many requests. Please try again shortly.';
                                    setSubscribeMessage({ state: true, message: message, class: 'error' });
                                    break;
                                default:
                                    message = feedbackErrorText;
                                    setSubscribeMessage({ state: true, message: message, class: 'error' });
                            }
                        }
                        setLoading(false);
                    });
            } else {
                signUp_DLX_V3Async(
                    { callerContext: context.actionContext },
                    '',
                    '',
                    data.email,
                    '', // Password - empty for loyalty signup
                    !isUS ? data.phone_number : `+1${data.phone_number}`,
                    true,
                    data.marketing,
                    onlyGenericMarketingFlag ? data.marketing : data.profiling,
                    onlyGenericMarketingFlag ? false : data.thirdparty,
                    localeString,
                    birthday_slash,
                    data.postal_code,
                    store_code ? `Store_${store_code}` : 'Store_fallback',
                    regionCRM
                )
                    .then(result => {
                        // Log debugging information
                        console.log('SignUp_DLX_V3 response:', {
                            status: result.status,
                            messageKey: result.messageKey,
                            errorCode: result.errorCode,
                            correlationId: result.correlationId,
                            checkpoints: result.checkpoints
                        });

                        // Handle error scenarios based on status and messageKey
                        if (result.status && result.status !== 200) {
                            // Map error codes and messageKeys to user messages
                            switch (result.errorCode) {
                                case 'LOYALTY_SCHEME_NOT_AVAILABLE':
                                    throw new Error('loyalty_not_available');
                                case 'DUPLICATE_ENROLMENT': {
                                    // Extract card number for duplicate enrollment
                                    const existingCardNumber = result.loyaltyCards?.[0]?.CardNumber;
                                    if (existingCardNumber) {
                                        throw new Error(`already_existing:${existingCardNumber}`);
                                    } else {
                                        throw new Error('already_existing');
                                    }
                                }
                                case 'RTS_UNAVAILABLE':
                                    throw new Error('service_unavailable');
                                case 'GRAPH_THROTTLED':
                                    throw new Error('throttled');
                                default:
                                    throw new Error(result.errorCode || 'unknown_error');
                            }
                        }

                        // Handle success scenarios based on messageKey
                        switch (result.messageKey) {
                            case 'vclub.success':
                                // Full success - loyalty card issued
                                if (
                                    result.loyaltyCards &&
                                    result.loyaltyCards.length > 0 &&
                                    result.loyaltyCards[0].OmOperatingUnitId !== 0
                                ) {
                                    if (result.loyaltyCards[0].CardNumber?.length) {
                                        console.log('Loyalty card issued:', result.loyaltyCards[0].CardNumber);
                                        setLoyaltyCardNumber(result.loyaltyCards[0].CardNumber ?? null);
                                    }
                                    message = feedbackSuccess ?? 'Thank you! Please check your email';
                                    setSubscribeMessage({
                                        state: true,
                                        message: message,
                                        class: 'success'
                                    });
                                } else if (result.customerGuid && result.customerGuid.length > 0) {
                                    // New email, card being generated
                                    console.log('New customer GUID:', result.customerGuid);
                                    message = feedbackSuccess ?? 'Thank you! Please check your email';
                                    setSubscribeMessage({
                                        state: true,
                                        message: message,
                                        class: 'success'
                                    });
                                } else if (result.accountNum?.length === 36) {
                                    // Account not synced yet
                                    console.log('Account not synced yet:', result.accountNum);
                                    throw new Error('generating');
                                } else if (result.accountNum?.length === 10) {
                                    // Account synced with FO number
                                    console.log('Account synced:', result.accountNum);
                                    if (
                                        result.loyaltyCards &&
                                        result.loyaltyCards.length > 0 &&
                                        result.loyaltyCards[0].OmOperatingUnitId !== 0
                                    ) {
                                        if (result.loyaltyCards[0].CardNumber?.length) {
                                            console.log('Loyalty card issued');
                                            setLoyaltyCardNumber(result.loyaltyCards[0].CardNumber ?? null);
                                        }
                                        message = feedbackSuccess ?? 'Thank you! Please check your email';
                                        setSubscribeMessage({
                                            state: true,
                                            message: message,
                                            class: 'success'
                                        });
                                    } else {
                                        console.log('Loyalty card already existing');
                                        throw new Error('already_existing');
                                    }
                                } else {
                                    message = feedbackSuccess ?? 'Thank you! Please check your email';
                                    setSubscribeMessage({
                                        state: true,
                                        message: message,
                                        class: 'success'
                                    });
                                }
                                break;
                            case 'vclub.newsletterOnly':
                                // Partial success - newsletter only
                                message = feedbackNewsletterOnly ?? 'Newsletter subscription confirmed. Loyalty card was not issued.';
                                setSubscribeMessage({
                                    state: true,
                                    message: message,
                                    class: 'generic'
                                });
                                break;
                            case 'vclub.duplicate': {
                                // Duplicate enrollment - show existing card number
                                const existingCardNumber = result.loyaltyCards?.[0]?.CardNumber;
                                if (existingCardNumber) {
                                    throw new Error(`already_existing:${existingCardNumber}`);
                                } else {
                                    throw new Error('already_existing');
                                }
                            }
                            default:
                                message = feedbackSuccess ?? 'Thank you! Please check your email';
                                setSubscribeMessage({
                                    state: true,
                                    message: message,
                                    class: 'success'
                                });
                        }

                        setLoading(false);
                        if (store_kiosk && result.status === 200) {
                            setTimeout(() => {
                                window.location.href = `https://${hostname}/${
                                    sitePath ? `${sitePath}/` : ''
                                }loyalty-region-selection?${queryParameter}=${store_code}&kiosk=true`;
                            }, 5000);
                        }
                    })
                    .catch(err => {
                        console.error('SignUp_DLX_V3 error:', err);
                        const errorMessage = err.message || '';

                        // Check if error contains card number (format: "already_existing:V00992890")
                        if (errorMessage.startsWith('already_existing:')) {
                            const cardNumber = errorMessage.split(':')[1];
                            message = feedbackAlreadySubscribed ?? 'The inserted email is already subscribed to our loyalty program.';
                            message += ` ${cardNumber}`;
                            setSubscribeMessage({
                                state: true,
                                message: message,
                                class: 'error'
                            });
                        } else {
                            switch (errorMessage) {
                                case 'generating':
                                    message =
                                        feedbackCardBeingGenerated ?? 'Your card is already being generated, thank you for your patience.';
                                    setSubscribeMessage({
                                        state: true,
                                        message: message,
                                        class: 'generic'
                                    });
                                    break;
                                case 'already_existing':
                                    message =
                                        feedbackAlreadySubscribed ?? 'The inserted email is already subscribed to our loyalty program.';
                                    setSubscribeMessage({
                                        state: true,
                                        message: message,
                                        class: 'error'
                                    });
                                    break;
                                case 'loyalty_not_available':
                                    message = feedbackLoyaltyNotAvailable ?? 'Loyalty is not available for this store.';
                                    setSubscribeMessage({
                                        state: true,
                                        message: message,
                                        class: 'error'
                                    });
                                    break;
                                case 'service_unavailable':
                                    message =
                                        feedbackServiceUnavailable ?? "We're having trouble connecting to the service. Please try again.";
                                    setSubscribeMessage({
                                        state: true,
                                        message: message,
                                        class: 'error'
                                    });
                                    break;
                                case 'throttled':
                                    message = feedbackThrottled ?? 'Too many requests. Please try again shortly.';
                                    setSubscribeMessage({
                                        state: true,
                                        message: message,
                                        class: 'error'
                                    });
                                    break;
                                default:
                                    message = feedbackErrorText;
                                    setSubscribeMessage({
                                        state: true,
                                        message: message,
                                        class: 'error'
                                    });
                            }
                        }
                        setLoading(false);
                    });
            }
        }

        if (enableSignUp) {
            signUp_DLX_V3Async(
                { callerContext: context.actionContext },
                data.name,
                data.lastname,
                data.email,
                data.password,
                !isUS ? data.phone_number || '' : `+1${data.phone_number}`,
                true,
                data.marketing,
                onlyGenericMarketingFlag ? data.marketing : data.profiling,
                onlyGenericMarketingFlag ? false : data.thirdparty,
                localeString,
                birthday_slash,
                data.postal_code,
                store_code ? `Store_${store_code}` : 'Store_fallback',
                regionCRM
            )
                .then(result => {
                    console.log('SignUp_DLX_V3 response (enableSignUp):', {
                        status: result.status,
                        messageKey: result.messageKey,
                        errorCode: result.errorCode,
                        correlationId: result.correlationId,
                        checkpoints: result.checkpoints
                    });

                    // Handle error scenarios based on status and errorCode
                    if (result.status && result.status !== 200) {
                        // Map error codes to user messages
                        switch (result.errorCode) {
                            case 'CUSTOMER_EXISTS':
                            case 'DUPLICATE_ENROLMENT': {
                                // Extract card number for duplicate enrollment
                                const existingCardNumber = result.loyaltyCards?.[0]?.CardNumber;
                                if (existingCardNumber) {
                                    throw new Error(`already_existing:${existingCardNumber}`);
                                } else {
                                    throw new Error('already_existing');
                                }
                            }
                            case 'LOYALTY_SCHEME_NOT_AVAILABLE':
                                throw new Error('loyalty_not_available');
                            case 'RTS_UNAVAILABLE':
                                throw new Error('service_unavailable');
                            case 'GRAPH_THROTTLED':
                                throw new Error('throttled');
                            default:
                                throw new Error(result.errorCode || 'unknown_error');
                        }
                    }

                    // Handle success scenarios based on messageKey
                    switch (result.messageKey) {
                        case 'vclub.success':
                            if (result.loyaltyCards && result.loyaltyCards.length > 0 && result.loyaltyCards[0].OmOperatingUnitId !== 0) {
                                if (result.loyaltyCards[0].CardNumber?.length) {
                                    console.log('Loyalty card issued');
                                    setLoyaltyCardNumber(result.loyaltyCards[0].CardNumber ?? null);
                                    setLoyaltyEnrollmentDate(result.loyaltyCards[0].LoyaltyEnrollmentDateLocal ?? null);
                                }
                                message = feedbackSuccess ?? 'Thank you! Please check your email';
                                setSubscribeMessage({ state: true, message: message, class: 'success' });
                            } else {
                                console.log('Loyalty card already existing');
                                throw new Error('already_existing');
                            }
                            break;
                        case 'vclub.newsletterOnly':
                            message = feedbackNewsletterOnly ?? 'Newsletter subscription confirmed. Loyalty card was not issued.';
                            setSubscribeMessage({ state: true, message: message, class: 'generic' });
                            break;
                        case 'vclub.duplicate': {
                            // Duplicate enrollment - show existing card number
                            const existingCardNumber = result.loyaltyCards?.[0]?.CardNumber;
                            if (existingCardNumber) {
                                throw new Error(`already_existing:${existingCardNumber}`);
                            } else {
                                throw new Error('already_existing');
                            }
                        }
                        default:
                            message = feedbackSuccess ?? 'Thank you! Please check your email';
                            setSubscribeMessage({ state: true, message: message, class: 'success' });
                    }

                    setLoading(false);
                })
                .catch(err => {
                    console.error('SignUp_DLX_V3 error (enableSignUp):', err);
                    const errorMessage = err.message || '';

                    // Check if error contains card number (format: "already_existing:V00992890")
                    if (errorMessage.startsWith('already_existing:')) {
                        const cardNumber = errorMessage.split(':')[1];
                        message = feedbackAlreadySubscribed ?? 'The inserted email is already subscribed to our loyalty program.';
                        message += ` ${cardNumber}`;
                        setSubscribeMessage({ state: true, message: message, class: 'error' });
                    } else {
                        switch (errorMessage) {
                            case 'already_existing':
                                message = feedbackAlreadySubscribed ?? 'The inserted email is already subscribed to our loyalty program.';
                                setSubscribeMessage({ state: true, message: message, class: 'error' });
                                break;
                            case 'loyalty_not_available':
                                message = feedbackLoyaltyNotAvailable ?? 'Loyalty is not available for this store.';
                                setSubscribeMessage({ state: true, message: message, class: 'error' });
                                break;
                            case 'service_unavailable':
                                message = feedbackServiceUnavailable ?? "We're having trouble connecting to the service. Please try again.";
                                setSubscribeMessage({ state: true, message: message, class: 'error' });
                                break;
                            case 'throttled':
                                message = feedbackThrottled ?? 'Too many requests. Please try again shortly.';
                                setSubscribeMessage({ state: true, message: message, class: 'error' });
                                break;
                            default:
                                message = feedbackErrorText;
                                setSubscribeMessage({ state: true, message: message, class: 'error' });
                        }
                    }

                    setLoading(false);
                });
        }
    };

    // React.useEffect(() => {
    //     console.groupCollapsed('Debug LoyaltySignUp form state');
    //     console.log('isValid', isValid);
    //     console.log('disabled_state', disabled_state);
    //     console.log('unchecked_state', unchecked_state);
    //     console.log('phoneErrorMessage', phoneErrorMessage);
    //     console.log(
    //         `!isValid || disabled_state || unchecked_state || !!phoneErrorMessage:`,
    //         !isValid || disabled_state || unchecked_state || !!phoneErrorMessage
    //     );
    //     console.groupEnd();
    // }, [isValid, disabled_state, unchecked_state, phoneErrorMessage]);

    // Debug US 9189
    React.useEffect(() => {
        console.log({ subscribeMessage, loyaltyCardNumber, submittedEmailAddress });
    }, [subscribeMessage, loyaltyCardNumber, submittedEmailAddress]);

    const currentChannelCountryRegionISOCode = props.context.request.channel?.ChannelCountryRegionISOCode || 'IT';

    return (
        <div className='loyalty-store-sign-up-form'>
            {!subscribeMessage.state ? (
                <form onSubmit={handleSubmit(_submit)}>
                    {loading ? <div className={'loading'} style={{ margin: 'auto', marginBottom: '1rem' }} /> : <span></span>}
                    <div className='prominent-fields'>
                        {emailAddressLabelText && emailErrortext ? (
                            <div className='row email-div'>
                                <div className='mandatory-container'>
                                    <label htmlFor='email'>{emailAddressLabelText}</label>
                                    <span className='mandatory-container__red-text'>*{props.resources.mandatoryLabel}</span>
                                </div>
                                <input key='email' name='email' ref={register} />
                                {/* {watchEmail && watchEmail.includes('@') && watchEmail.endsWith('.con') && (
                                    <>
                                        <p className='dotcon'>
                                            Maybe you meant {watchEmail.slice(0, -3)}
                                            <b>com</b>&nbsp;&nbsp;
                                        </p>
                                    </>
                                )} */}
                                {/* For future scalability: Array with forbidden domains and .any in the endsWith? */}
                                {(errors.email || (watchEmail && watchEmail.endsWith('.con'))) && (
                                    <span className='email-error'>{emailErrortext}</span>
                                )}
                            </div>
                        ) : null}

                        {phoneLabelText && isUS ? (
                            <div className='row phone-number-div' id='phone_number_div'>
                                <div className='mandatory-container'>
                                    <label htmlFor='phone_number_div'>{props.resources.phoneLabelText}</label>
                                    <span className='mandatory-container__red-text'>*{props.resources.mandatoryLabel}</span>
                                </div>
                                <div className='phone-div'>
                                    <div className='phone-prefix'>
                                        <span>+1</span>
                                    </div>
                                    <input
                                        type='tel'
                                        key='phone_number'
                                        id='phone_number'
                                        name='phone_number'
                                        placeholder='(###) ### - ####'
                                        ref={register}
                                        // ref={
                                        //     localeString === 'en-us'
                                        //         ? register({
                                        //               required: true,
                                        //               pattern: /^\\ ( [2-9] {1} [0-9] {2}\\) [ ]? [0-9] {3}[-]?[0-9] {4}$/,
                                        //               maxLength: 15
                                        //           })
                                        //         : register({ required: false, maxLength: 15 })
                                        // }
                                    />
                                </div>
                                {errors.phone_number && (
                                    <span className='phone-error'>{emailErrortext.replace('email', 'phone number')}</span>
                                )}
                                {phoneNumberSubtext ? <p className='optional-subtext'>{phoneNumberSubtext}</p> : null}
                            </div>
                        ) : null}

                        {effectiveHtmlGenericMarketing && privacyPolicyURL && showFlagGenericMarketing ? (
                            <div className='row checkbox-line agreements'>
                                <input
                                    key='marketing'
                                    type='checkbox'
                                    id='marketing'
                                    name='marketing'
                                    ref={register}
                                    defaultChecked={onlyGenericMarketingFlag}
                                />
                                <label htmlFor='marketing' className='agreements'>
                                    <MsDyn365.RichTextComponent
                                        text={
                                            textGenericMarketing && onlyGenericMarketingFlag
                                                ? textGenericMarketing
                                                : format(effectiveHtmlGenericMarketing, privacyPolicyURL)
                                        }
                                    />
                                </label>
                            </div>
                        ) : null}

                        {effectiveHtmlProfiling && privacyPolicyURL && showFlagProfiling ? (
                            <div className='row checkbox-line agreements'>
                                <input id='profiling' key='profiling' type='checkbox' name='profiling' ref={register} />
                                <label htmlFor='profiling' className='agreements'>
                                    <MsDyn365.RichTextComponent text={format(effectiveHtmlProfiling, privacyPolicyURL)} />
                                </label>
                            </div>
                        ) : null}

                        {effectiveHtmlThirdParty && privacyPolicyURL && showFlagThirdParty ? (
                            <div className='row checkbox-line agreements'>
                                <input id='thirdparty' key='thirdparty' type='checkbox' name='thirdparty' ref={register} />
                                <label htmlFor='thirdparty' className='agreements thirdparty'>
                                    <MsDyn365.RichTextComponent text={format(effectiveHtmlThirdParty, privacyPolicyURL)} />
                                </label>
                            </div>
                        ) : null}
                    </div>

                    <div className='secondary-fields'>
                        {additionalFieldsHeading ? <h4>{additionalFieldsHeading}</h4> : null}

                        {(enableSignUp || isAU) && (
                            <>
                                <div className='row name-div' id='name_div'>
                                    <div className='mandatory-container'>
                                        <label htmlFor='name'>{props.resources.optionalFirstName}</label>
                                        {!isAU && <span className='mandatory-container__red-text'>*{props.resources.mandatoryLabel}</span>}
                                    </div>
                                    <input type='text' id='name' name='name' ref={register} />
                                    {/* @ts-ignore */}
                                    {errors.name && <p className='field-error'>{props.resources.addressNameRequiredErrorMessage}</p>}
                                </div>

                                <div className='row lastname-div' id='lastname_div'>
                                    <div className='mandatory-container'>
                                        <label htmlFor='lastname'>{props.resources.optionalLastName}</label>
                                        {!isAU && <span className='mandatory-container__red-text'>*{props.resources.mandatoryLabel}</span>}
                                    </div>
                                    <input type='text' key='lastname' id='lastname' name='lastname' ref={register} />
                                    {/* @ts-ignore */}
                                    {errors.lastname && <p className='field-error'>{props.resources.lastnameMandatoryLabel}</p>}
                                </div>

                                {!isAU && (
                                    <div className='row password-div' id='password_div'>
                                        <div className='mandatory-container'>
                                            <label htmlFor='password'>{props.resources.passwordLabelText}</label>
                                            <span className='mandatory-container__red-text'>*{props.resources.mandatoryLabel}</span>
                                        </div>
                                        <div className='input-group'>
                                            <input
                                                className='input-group__text'
                                                type={showPassword ? 'text' : 'password'}
                                                key='password'
                                                id='password'
                                                name='password'
                                                ref={register}
                                            />
                                            <button
                                                onClick={() => setShowPassword(curValue => !curValue)}
                                                className='input-group__icon-button'
                                                type='button'
                                            >
                                                <i
                                                    aria-label={
                                                        showPassword ? props.resources.hidePasswordLabel : props.resources.showPasswordLabel
                                                    }
                                                    className={`input-group__icon ${
                                                        showPassword ? 'input-group__icon--eye-off' : 'input-group__icon--eye'
                                                    }`}
                                                />
                                            </button>
                                        </div>
                                        {/* @ts-ignore */}
                                        {errors.password && (
                                            <p
                                                className='field-error'
                                                dangerouslySetInnerHTML={{ __html: props.resources.passwordRequirementsLabel }}
                                            ></p>
                                        )}
                                    </div>
                                )}
                            </>
                        )}

                        {phoneLabelText && !isUS ? (
                            <div className='row phone-number-div' id='phone_number_div'>
                                <div className='mandatory-container'>
                                    <label htmlFor='phoneInputComponent'>{phoneLabelText}</label>
                                    {isAU && <span className='mandatory-container__red-text'>*{props.resources.mandatoryLabel}</span>}
                                </div>

                                <PhoneInputComponent
                                    channelCountryRegionISOCode={currentChannelCountryRegionISOCode}
                                    isCountryCodeHidden={props.config.hideCountryCodeSelector || false}
                                    phoneInputId={'phoneInputComponent'}
                                    useDefaultCountryCode={props.config.useDefaultCountryCode}
                                    defaultCountryCode={props.config.defaultCountryCode as any}
                                    resources={{
                                        phoneValidationErrorMin: props.resources.phoneValidationErrorMin,
                                        phoneValidationErrorMax: props.resources.phoneValidationErrorMax,
                                        phoneValidationErrorNumeric: props.resources.phoneValidationErrorNumeric
                                    }}
                                    onPhoneNumberChange={(phoneNumber: string, fullPhoneNumber: string, hasValidationError: boolean) => {
                                        if (!hasValidationError) {
                                            setPhoneErrorMessage('');
                                        } else {
                                            setPhoneErrorMessage(props.resources.phoneValidationErrorMessage);
                                        }
                                        const phoneNumberHidden: HTMLInputElement = document.getElementById(
                                            'phone_number'
                                        ) as HTMLInputElement;

                                        phoneNumberHidden.value = phoneNumber ? fullPhoneNumber : '';
                                    }}
                                />
                                <input type='hidden' ref={register} id='phone_number' name='phone_number' style={{ display: 'none' }} />
                                {phoneNumberSubtext ? <p className='optional-subtext mt-1'>{phoneNumberSubtext}</p> : null}
                            </div>
                        ) : null}

                        {birthdayLabelText && (
                            <div className='row birthday-div' id='birthday_div'>
                                <div className='mandatory-container'>
                                    <label htmlFor='birthday'>
                                        {birthdayLabelText}
                                        <span className='date-mask'>({getDateMask(localeString)})</span>
                                    </label>
                                    {isAU && <span className='mandatory-container__red-text'>*{props.resources.mandatoryLabel}</span>}
                                </div>
                                <DateOfBirthInput
                                    id='birthday'
                                    name='birthday'
                                    locale={localeString}
                                    onChange={handleBirthdayChange}
                                    onBlur={handleBirthdayBlur}
                                    preventPaste={true}
                                />
                                <input
                                    type='hidden'
                                    key='birthday'
                                    id='birthday'
                                    name='birthday'
                                    ref={register}
                                    style={{ display: 'none' }}
                                />
                                {showInvalidBirthdayFormatError && (
                                    <p className='field-error'>
                                        {(props.resources as any).birthdayErrorMessage || 'Invalid birth date format'}
                                    </p>
                                )}
                                {showMinAgeError && (
                                    <p className='field-error'>
                                        {(props.resources as any).minAgeErrorLabel || 'You must be at least 18 years old'}
                                    </p>
                                )}
                                {birthdaySubtext ? <p className='optional-subtext'>{birthdaySubtext}</p> : null}
                            </div>
                        )}

                        {addressZipCodeLabelOptional ? (
                            <div className='row postal-code-div' id='postal_code_div'>
                                <label htmlFor='postal_code'>{addressZipCodeLabelOptional}</label>
                                <input type='text' key='postal_code' id='postal_code' name='postal_code' maxLength={9} ref={register} />
                                {postalCodeSubtext ? <p className='optional-subtext'>{postalCodeSubtext}</p> : null}
                            </div>
                        ) : null}
                    </div>

                    {!isUS ? (
                        // default (and AU by default)
                        loyaltyStoreSignUpMandatoryAgreement ? (
                            <div className='row mandatory-agreement-div'>
                                <input
                                    type='checkbox'
                                    className='mandatory-agreement-check'
                                    ref={check_ref}
                                    onClick={() => {
                                        setUncheckedState(!check_ref.current?.checked!);
                                    }}
                                />
                                <label
                                    className={isAU ? 'mandatory-agreement-au' : undefined}
                                    // @ts-ignore
                                    dangerouslySetInnerHTML={{ __html: loyaltyStoreSignUpMandatoryAgreement }}
                                />
                                {/* TODO change to MsDyn365.RichTextComponent to avoid setHTML? */}
                                {/* </input> */}
                            </div>
                        ) : null
                    ) : // US
                    loyaltyStoreSignUpMandatoryAgreement ? (
                        <div className='row mandatory-agreement-div'>
                            {/* TODO change to MsDyn365.RichTextComponent to avoid setHTML? */}
                            <div
                                className='mandatory-agreement agreements'
                                // @ts-ignore
                                dangerouslySetInnerHTML={{ __html: loyaltyStoreSignUpMandatoryAgreement }}
                            ></div>
                        </div>
                    ) : null}

                    <div className='row'>
                        <button
                            type='submit'
                            disabled={
                                !isValid ||
                                disabled_state ||
                                unchecked_state ||
                                !!phoneErrorMessage ||
                                showInvalidBirthdayFormatError ||
                                showMinAgeError
                            }
                            className={`status__${subscribeMessage.class} ${
                                !isValid ||
                                disabled_state ||
                                unchecked_state ||
                                !!phoneErrorMessage ||
                                showInvalidBirthdayFormatError ||
                                showMinAgeError
                                    ? 'btn-disabled'
                                    : ''
                            }`}
                        >
                            {loading && loyaltyStoreSignUpSubmitButtonLabel ? (
                                <div className={'loading'} />
                            ) : (
                                <span>{loyaltyStoreSignUpSubmitButtonLabel}</span>
                            )}
                        </button>

                        {phoneErrorMessage && (
                            <div className='ms-sign-up__item-error error itemLevel' role='alert'>
                                <p style={{ color: '#C13533' }}>{phoneErrorMessage}</p>
                            </div>
                        )}
                    </div>
                </form>
            ) : isMobileDevice &&
              loyaltyCardNumber &&
              loyaltyCardNumber.length > 0 &&
              submittedEmailAddress &&
              submittedEmailAddress.length > 0 ? (
                // Here goes the response from the success request
                <div className='loyalty-store-signup-success-container'>
                    <div className={`msc-alert msc-alert-${subscribeMessage.class}`} role='alert'>
                        <div className='loyalty-store-signup-thankyou'>
                            {/* <p>DEBUG test Protip if kiosk error something like show this to a sales assistant in the store</p> */}
                            <div className='message-container'>
                                <p>{joinedLoyaltyThankYouHeader}</p>
                            </div>
                        </div>
                    </div>
                    <div className='qr-code-container'>
                        <img
                            className='qr-code-image'
                            src={`https://qrcodegeneratorloyalty.azurewebsites.net/api/generateqr?qrtext=${loyaltyCardNumber}`}
                            width='115'
                        />
                    </div>
                    <div className='card-number-container'>
                        <p>
                            {vClubCard}: <strong className='loyalty-card-number'>{loyaltyCardNumber}</strong>
                        </p>
                    </div>
                    {loyaltyBarcodeImage?.src && (
                        <div className='barcode-container'>
                            <p className='barcode-text'>{textWithLineBreaks(couponCodeText)}</p>
                            <img className='barcode-image' src={loyaltyBarcodeImage.src} alt={loyaltyCouponCode} />
                            <p className='barcode-text'>{textWithLineBreaks(loyaltyBarcodeText)}</p>
                        </div>
                    )}
                    {/* Hide add to wallet buttons because user creation happens with a delay */}
                    {/* <div className='wallet-buttons-container'>
                        <div className='wallet-button'>
                            <a
                                // eslint-disable-next-line prettier/prettier
                                className='wallet-button-link'
                                href={`https://func-applewallet-prod.azurewebsites.net/venchiloyalty/v1/passes/getpass?program=${programString}&loyaltyCardNumber=${loyaltyCardNumber}&email=${submittedEmailAddress}`}
                                data-msdyn-tracking-id='bada53d825861715766699754'
                            >
                                <img
                                    className='wallet-button-image'
                                    src='https://assets-eur.mkt.dynamics.com/9e61c47c-15ef-44e6-9fdd-26cc7f31a8fd/digitalassets/images/0bdcf2a4-a012-ef11-9f89-6045bd9d2b92?ts=3D638513634711035164'
                                />
                            </a>
                        </div>
                        <div className='wallet-button'>
                            <a
                                // eslint-disable-next-line prettier/prettier
                                className='wallet-button-link'
                                href={`https://func-googlewallet-prod.azurewebsites.net/venchiloyalty/v1/passes/getpass?program=${programString}&loyaltyCardNumber=${loyaltyCardNumber}&email=${submittedEmailAddress}`}
                                data-msdyn-tracking-id='09c9291f628921715766745922'
                            >
                                <img
                                    className='wallet-button-image'
                                    src='https://assets-eur.mkt.dynamics.com/9e61c47c-15ef-44e6-9fdd-26cc7f31a8fd/digitalassets/images/623cf797-a012-ef11-9f89-6045bd9d2b92?ts=3D638513634445092143'
                                />
                            </a>
                        </div>
                    </div> */}
                    <div className='enrollment-date-container'>
                        <p>
                            {loyaltyRegistrationDateText}:{' '}
                            <time className='enrollment-date' dateTime={loyaltyEnrollmentDate?.toISOString()}>
                                {loyaltyEnrollmentDate?.toLocaleDateString(localeString)}
                            </time>
                        </p>
                    </div>
                    {/* {!store_kiosk && loyaltyVCLUBDetailsLinkText && subscribeMessage.class !== 'error' ? (
                                <a href={loyalty_URL}>{loyaltyVCLUBDetailsLinkText}</a>
                            ) : null} */}
                </div>
            ) : (
                <div className={`msc-alert msc-alert-${subscribeMessage.class}`} role='alert'>
                    <div className='message-container'>
                        <p>{subscribeMessage.message}</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LoyaltyStoreSignUpView;
